import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getNavigationItems, getPermissions } from '../../config/roles';
import { useVersionCheck } from '../../hooks/useVersionCheck';
import clsx from 'clsx';
import { Bars3Icon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import AboutModal from './AboutModal';

export default function Navigation() {
  const { currentAgent, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const { currentVersion, currentBuildNumber, latestVersion, latestBuildNumber, updateAvailable, checking, checkVersion, forceUpdate } = useVersionCheck();

  if (!isAuthenticated) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm px-4 py-2 shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center">
            <div className="flex items-center">
              <img 
                src="/plsss_logo.jpeg" 
                alt="PLSSS Logo" 
                className="h-8 w-auto"
              />
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Get navigation items based on user role, permissions, and category
  const role = currentAgent?.role || 'user';
  const permissions = getPermissions(role);
  const category = currentAgent?.category || 'POP';
  const navItems = getNavigationItems(role, permissions, category);
  
  // Add WhatsApp link if user has WhatsApp access
  if (currentAgent?.apps?.whatsapp?.allowed) {
    navItems.push({
      path: '/whatsapp',
      label: '📱 WhatsApp',
      id: 'whatsapp',
      requiredPermission: null
    });
  }

  const handleNavClick = (item) => {
  if (item.action && window[item.action]) {
    // Handle modal actions
    window[item.action]();
  } else {
    // Handle regular navigation
    navigate(item.path);
  }
  // Close mobile menu after navigation
  setIsMobileMenuOpen(false);
  // Close user dropdown after navigation
  setIsUserDropdownOpen(false);
};

const isActive = (path) => {
  // Handle special case for modal
  if (path === '#createagent') {
    return false; // Modal is never "active" in navigation
  }
  return location.pathname === path;
};

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm px-4 py-2 shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/plsss_logo.jpeg" 
                alt="PLSSS Logo" 
                className="h-7 w-auto sm:h-8"
              />
            </div>

            {/* Desktop Navigation - Hidden on mobile */}
            <div className="hidden lg:flex items-center space-x-3">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className={clsx(
                    'px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 border-2',
                    isActive(item.path) 
                      ? 'bg-[#022b73] text-[#F7FBFC] border-[#022b73] shadow-md' 
                      : 'bg-[#F7FBFC]/90 text-[#022b73] border-[#F7FBFC]/50 hover:bg-[#F7FBFC] hover:border-[#022b73] hover:shadow-md'
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Agent Info and Logout - Desktop */}
            <div className="hidden lg:flex items-center space-x-3">
              {/* User Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="flex items-center space-x-2 bg-[#F7FBFC]/90 text-[#022b73] px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 border-2 border-[#F7FBFC]/50 hover:bg-[#F7FBFC] hover:border-[#022b73] hover:shadow-md"
                >
                  <span className="font-medium">{currentAgent?.name || 'Agent'}</span>
                  {isUserDropdownOpen ? (
                    <ChevronUpIcon className="h-4 w-4" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4" />
                  )}
                </button>

                {/* User Dropdown Menu */}
                {isUserDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setIsAboutModalOpen(true);
                          setIsUserDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-[#022b73] hover:bg-gray-100 transition-colors"
                      >
                        ℹ️ About
                      </button>
                      <button
                        onClick={() => {
                          logout();
                          setIsUserDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        🚪 Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-2">
              {/* Mobile Agent Info */}
              <div className="text-[#022b73] font-semibold text-xs max-w-[100px] truncate">
                <span className="font-medium">{currentAgent?.name || 'Agent'}</span>
              </div>
              
              {/* Hamburger Menu */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-[#022b73] hover:bg-[#F7FBFC] transition-colors"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Mobile Menu Panel */}
          <div className="fixed right-0 top-0 h-full w-80 max-w-full bg-white shadow-xl">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#022b73]">{currentAgent?.name || 'Agent'}</h3>
                  <p className="text-sm text-gray-600">ID: {currentAgent?.agent_id || 'N/A'}</p>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg text-[#022b73] hover:bg-[#F7FBFC] transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Navigation Items */}
            <div className="p-4 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className={clsx(
                    'w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 border-2',
                    isActive(item.path) 
                      ? 'bg-[#022b73] text-[#F7FBFC] border-[#022b73]' 
                      : 'bg-[#F7FBFC]/90 text-[#022b73] border-[#F7FBFC]/50 hover:bg-[#F7FBFC] hover:border-[#022b73]'
                  )}
                >
                  {item.label}
                </button>
              ))}
              
              {/* About Button */}
              <button
                onClick={() => {
                  setIsAboutModalOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 bg-gray-50 text-[#022b73] border-2 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
              >
                ℹ️ About
              </button>
              
              {/* Logout Button */}
              <button
                onClick={() => {
                  logout();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 bg-red-50 text-red-600 border-2 border-red-200 hover:bg-red-100 hover:border-red-300"
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* About Modal */}
      <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
    </>
  );
}
